/**
 * Billing Dashboard Page
 * 
 * Comprehensive billing analytics:
 * - Monthly revenue (invoiced vs collected)
 * - Pending and overdue amounts
 * - Collection rate
 * - Revenue by plan
 * - Monthly evolution charts
 * - Revenue forecast
 * - Top clients
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { api } from '../../App';
import { toast } from 'sonner';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle,
  FileText,
  Users,
  RefreshCw,
  Calendar,
  BarChart3,
  PieChart,
  ArrowRight,
  Zap,
  Target,
  CreditCard
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart
} from 'recharts';

// Format currency
const formatCurrency = (amount, currency = 'MAD') => {
  return `${parseFloat(amount || 0).toLocaleString('fr-FR')} ${currency}`;
};

// KPI Card component
const KPICard = ({ title, value, subtitle, icon: Icon, color = 'blue', trend, trendValue }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    green: 'bg-green-50 text-green-600 border-green-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
    red: 'bg-red-50 text-red-600 border-red-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    gray: 'bg-gray-50 text-gray-600 border-gray-100'
  };

  return (
    <Card className="relative overflow-hidden">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
            {trend && (
              <div className={`flex items-center gap-1 mt-2 text-xs ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                {trend === 'up' ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                <span>{trendValue}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${colors[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Gauge component for collection rate
const GaugeChart = ({ value, label }) => {
  const radius = 80;
  const strokeWidth = 12;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (value / 100) * circumference;
  
  const color = value >= 80 ? '#22c55e' : value >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <div className="flex flex-col items-center">
      <svg width={radius * 2} height={radius * 2} className="transform -rotate-90">
        <circle
          stroke="#e5e7eb"
          fill="transparent"
          strokeWidth={strokeWidth}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <circle
          stroke={color}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ marginTop: '45px' }}>
        <span className="text-3xl font-bold">{value}%</span>
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
    </div>
  );
};

export default function BillingDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const { data: result } = await api.get(`/admin/billing/dashboard?year=${year}`);
      setData(result);
    } catch (error) {
      toast.error('Erreur lors du chargement du dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [year]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
        <p className="text-muted-foreground">Impossible de charger le dashboard</p>
        <Button onClick={fetchDashboard} className="mt-4">Réessayer</Button>
      </div>
    );
  }

  // Chart colors
  const CHART_COLORS = ['#6366F1', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  return (
    <div className="space-y-6" data-testid="billing-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboard Facturation</h1>
          <p className="text-muted-foreground">Vue d'ensemble des revenus et de la facturation</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={fetchDashboard} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={() => navigate('/admin/billing')} data-testid="goto-billing">
            <FileText className="h-4 w-4 mr-2" />
            Factures
          </Button>
        </div>
      </div>

      {/* KPI Cards Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Facturé ce mois"
          value={formatCurrency(data.currentMonth?.invoiced)}
          subtitle={data.currentMonth?.month}
          icon={FileText}
          color="blue"
        />
        <KPICard
          title="Encaissé ce mois"
          value={formatCurrency(data.currentMonth?.collected)}
          subtitle={data.currentMonth?.month}
          icon={CheckCircle}
          color="green"
        />
        <KPICard
          title="En attente"
          value={formatCurrency(data.pending?.amount)}
          subtitle={`${data.pending?.count} facture(s)`}
          icon={Clock}
          color="orange"
        />
        <KPICard
          title="En retard"
          value={formatCurrency(data.overdue?.amount)}
          subtitle={`${data.overdue?.count} facture(s)`}
          icon={AlertTriangle}
          color="red"
        />
      </div>

      {/* KPI Cards Row 2 - Yearly */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title={`Facturé ${year}`}
          value={formatCurrency(data.yearly?.invoiced)}
          icon={BarChart3}
          color="purple"
        />
        <KPICard
          title={`Encaissé ${year}`}
          value={formatCurrency(data.yearly?.collected)}
          icon={DollarSign}
          color="green"
        />
        <KPICard
          title="Revenu récurrent mensuel"
          value={formatCurrency(data.forecast?.monthly_recurring)}
          subtitle={`${data.forecast?.active_subscriptions} abonnements actifs`}
          icon={Zap}
          color="blue"
        />
        <KPICard
          title="Projection annuelle"
          value={formatCurrency(data.forecast?.projected_yearly)}
          subtitle="Estimation fin d'année"
          icon={Target}
          color="purple"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Evolution */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Évolution mensuelle
            </CardTitle>
            <CardDescription>Facturé vs Encaissé par mois</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={data.monthlyEvolution || []}>
                <defs>
                  <linearGradient id="invoicedGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="collectedGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22C55E" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  labelStyle={{ fontWeight: 'bold' }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="invoiced"
                  name="Facturé"
                  stroke="#6366F1"
                  fill="url(#invoicedGradient)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="collected"
                  name="Encaissé"
                  stroke="#22C55E"
                  fill="url(#collectedGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Collection Rate Gauge */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5" />
              Taux de recouvrement
            </CardTitle>
            <CardDescription>Factures payées / facturées</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center pt-4">
            <div className="relative">
              <GaugeChart value={data.collectionRate?.yearly || 0} label={`Année ${year}`} />
            </div>
            <div className="mt-8 w-full space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tout temps</span>
                <span className="font-medium">{data.collectionRate?.all_time || 0}%</span>
              </div>
              <Progress value={data.collectionRate?.all_time || 0} className="h-2" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue by Plan & Top Clients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Plan */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Revenus par plan
            </CardTitle>
            <CardDescription>Répartition des revenus {year}</CardDescription>
          </CardHeader>
          <CardContent>
            {data.revenueByPlan?.length > 0 ? (
              <div className="flex items-center gap-8">
                <ResponsiveContainer width={200} height={200}>
                  <RechartsPie>
                    <Pie
                      data={data.revenueByPlan}
                      dataKey="total"
                      nameKey="plan_name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {data.revenueByPlan.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.plan_color || CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </RechartsPie>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {data.revenueByPlan.map((plan, index) => (
                    <div key={plan.plan_id || index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: plan.plan_color || CHART_COLORS[index % CHART_COLORS.length] }}
                        />
                        <span className="text-sm">{plan.plan_name}</span>
                      </div>
                      <span className="text-sm font-medium">{formatCurrency(plan.total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">Aucune donnée</p>
            )}
          </CardContent>
        </Card>

        {/* Top Clients */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Top clients {year}
            </CardTitle>
            <CardDescription>Par revenu généré</CardDescription>
          </CardHeader>
          <CardContent>
            {data.topClients?.length > 0 ? (
              <div className="space-y-3">
                {data.topClients.map((client, index) => (
                  <div 
                    key={client.account_id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                        index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-gray-300'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{client.account_name}</p>
                        <p className="text-xs text-muted-foreground">{client.invoice_count} factures</p>
                      </div>
                    </div>
                    <span className="font-bold text-green-600">
                      {formatCurrency(client.total_revenue)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">Aucune donnée</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Invoices */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Factures récentes</CardTitle>
            <CardDescription>Dernières factures créées</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/billing')}>
            Voir tout
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {data.recentInvoices?.map((invoice) => (
              <div 
                key={invoice.id}
                className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                onClick={() => navigate(`/admin/billing?invoice=${invoice.id}`)}
              >
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm">{invoice.invoice_number}</p>
                    <p className="text-xs text-muted-foreground">{invoice.account_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className={
                    invoice.status === 'PAID' ? 'bg-green-50 text-green-700 border-green-200' :
                    invoice.status === 'OVERDUE' ? 'bg-red-50 text-red-700 border-red-200' :
                    invoice.status === 'SENT' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                    'bg-gray-50 text-gray-700 border-gray-200'
                  }>
                    {invoice.status}
                  </Badge>
                  <span className="font-medium">
                    {formatCurrency(invoice.amount_ttc, invoice.currency)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
